import { Controller, useForm } from 'react-hook-form';
import { CircularProgress, FormControl, InputLabel, NativeSelect, Stack } from '@mui/material';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useGetAiModelsByProviderIdQuery, useGetAiProvidersQuery } from 'services/aiProviderApi';

export function LlmAdvisorNodeForm({ id, data, onUpdateData }) {
  const { formatMessage: msg } = useIntl();

  const { providerCode, modelCode } = data;

  const { control, setValue, watch } = useForm({
    defaultValues: {
      providerCode: providerCode || '',
      modelCode: modelCode || ''
    }
  });

  // Watch for changes in form values
  const formValues = watch();

  // Fetch providers from API
  const { data: providers = [], isLoading: providersLoading } = useGetAiProvidersQuery();

  const filteredProviders = providers.filter((p) => p.displayableKey !== '***********');
  const selectedProvider =
    filteredProviders.find((p) => p.code === formValues.providerCode) ?? filteredProviders[0];
  const selectedProviderId = selectedProvider?.id;

  const { data: models = [], isLoading: modelsLoading } = useGetAiModelsByProviderIdQuery(
    selectedProviderId,
    { skip: !selectedProviderId }
  );

  // Sort models: favorites first, then alphabetically by label
  const sortedModels = [...models].sort((a, b) => {
    if (a.favorite && !b.favorite) return -1;
    if (!a.favorite && b.favorite) return 1;

    return a.label.localeCompare(b.label);
  });

  // Initialize provider and model when data loads
  useEffect(() => {
    if (filteredProviders.length > 0 && !formValues.providerCode) {
      setValue('providerCode', filteredProviders[0].code);
    }
  }, [filteredProviders, formValues.providerCode, sortedModels, setValue]);

  // Initialize model when data loads
  useEffect(() => {
    if (sortedModels.length > 0 && (!formValues.modelCode || formValues.modelCode === '-1')) {
      setValue('modelCode', sortedModels[0].code);
    }
    if (formValues.providerCode === '-1') {
      setValue('modelCode', '-1');
    }
  }, [sortedModels, formValues.providerCode, formValues.modelCode, setValue]);

  // Update form data when values change
  useEffect(() => {
    const updatedData = {
      ...data,
      providerCode: formValues.providerCode === '-1' ? '' : formValues.providerCode,
      modelCode: formValues.modelCode === '-1' ? '' : formValues.modelCode
    };

    onUpdateData({
      id,
      data: updatedData
    });
  }, [formValues, data, onUpdateData, id]);

  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      <Controller
        name="providerCode"
        control={control}
        rules={{ required: msg({ id: 'field.provider.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="provider-native-select">
              {msg({ id: 'field.provider.label' })}
            </InputLabel>
            {providersLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.provider.label' })}
                required
                inputProps={{
                  id: 'provider-native-select',
                  name: 'providerCode'
                }}
              >
                <option value="-1">Sélectionner un fournisseur</option>
                {filteredProviders.map((provider) => (
                  <option key={provider.code} value={provider.code}>
                    {provider.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />
      <Controller
        name="modelCode"
        control={control}
        rules={{ required: msg({ id: 'field.model.required' }) }}
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel variant="standard" htmlFor="model-native-select">
              {msg({ id: 'field.model.label' })}
            </InputLabel>
            {modelsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <NativeSelect
                {...field}
                label={msg({ id: 'field.model.label' })}
                required
                disabled={!selectedProviderId}
                inputProps={{
                  id: 'model-native-select',
                  name: 'modelCode'
                }}
              >
                <option value="-1">{msg({ id: 'field.model.default.label' })}</option>
                {sortedModels.map((model) => (
                  <option key={model.code} value={model.code}>
                    {model.label}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormControl>
        )}
      />
    </Stack>
  );
}

LlmAdvisorNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    providerCode: PropTypes.string,
    modelCode: PropTypes.string
  }),
  onUpdateData: PropTypes.func.isRequired
};
